import { getWebsiteUrl } from '@va/dashboard/selectors/core';
import { useCurrentPeriodFilter, useReportPreviousPeriod } from '@va/dashboard/util-hooks';
import { useLocalizationContext } from '@va/localization';
import { AppliedFilter, useFiltersContext } from '@va/shared/feature-filters';
import { useReportBlockCtx } from '@va/ui/components/report-block';
import { fontWeights, GrayGradientDivider, Heading3, Paragraph } from '@va/ui/design-system';
import { getLocalizedDate, getUrlWithoutProtocolAndTrailingSlash } from '@va/util/helpers';
import { useCustomizationContext } from '@va/util/misc';
import { Moment } from 'moment';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

/**
 * Replaces the RB header in the PNG / PDF exports with the context the image lacks: title, website, period and filters.
 * Pass it as the `exportSummary` RB node.
 */
export const ReportBlockExportSummary = () => {
  const { locale, translate } = useLocalizationContext();
  const { title, showPrevious } = useReportBlockCtx();
  const { getCustomValue } = useCustomizationContext();
  const { appliedFilters } = useFiltersContext();
  const websiteUrl = useSelector(getWebsiteUrl);
  const { from, until } = useCurrentPeriodFilter(true, false);
  const { from: previousFrom, until: previousUntil } = useReportPreviousPeriod(true, false);

  const formatPeriod = useCallback(
    (start: Moment, end: Moment) =>
      `${getLocalizedDate(start.unix(), locale)} - ${getLocalizedDate(end.unix(), locale)}`,
    [locale],
  );

  return (
    <div hidden className='visible-on-screenshot'>
      <Heading3 weight={fontWeights.semibold}>{getCustomValue('initialTitle', title)}</Heading3>
      <Paragraph
        className='mt-1'
        variant='body'
        variantSize='small'
        colorClassName='text-gray-devil'
        weight={fontWeights.medium}
      >
        {getUrlWithoutProtocolAndTrailingSlash(websiteUrl)} &middot; {formatPeriod(from, until)}
        {showPrevious && (
          <>
            {' '}
            &middot; {translate('reportBlock.export.comparedTo', { period: formatPeriod(previousFrom, previousUntil) })}
          </>
        )}
      </Paragraph>
      {appliedFilters.length > 0 && (
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <Paragraph variant='body' variantSize='small' colorClassName='text-gray-devil' weight={fontWeights.medium}>
            {translate('reportBlock.export.appliedFilters')}
          </Paragraph>
          {appliedFilters.map((filter) => (
            <AppliedFilter key={filter.id} filter={filter} size='medium' viewOnly disableEditing />
          ))}
        </div>
      )}
      <GrayGradientDivider className='mt-5' />
    </div>
  );
};
